/**
 * PR-H — overlay orchestrator (PR-K.1 split).
 *
 * Two responsibility groups, two components:
 *
 *   1. **`<Scene3DWorldOverlays>`** — mounted **inside** `<Canvas>`.
 *      Adds the THREE-mesh-backed overlays to the scene tree:
 *      - grid (`gridShader.ts`)
 *      - snap marker (`snapMarker.ts`)
 *      - polar guide (`polarGuide.ts`)
 *      - OTRACK alignment-path guide + acquired-point markers
 *        (`otrackGuide.ts` / `otrackMarker.ts`)
 *      - BEDIT frame (`beditFrame.ts`)
 *
 *   2. **`<Scene3DDomOverlays>`** — mounted as a **sibling** of
 *      `<Canvas>`. Renders the HTML overlays directly via JSX:
 *      - HTML crosshair (two full-viewport divs)
 *      - snap aperture box (`snapApertureBox.tsx`)
 *      - zoom rubber (`zoomRubber.tsx`)
 *      - print preview bounds (`printPreviewBounds.tsx`)
 *
 * # Why the split (PR-K.1 follow-up)
 *
 * PR-H originally mounted both world-space and screen-space overlays
 * inside a single `<Scene3DOverlayLayer>` child of `<Canvas>` and
 * portal'd the HTML half out via `ReactDOM.createPortal`. R3F's
 * reconciler crashed on the portal subtree with `R3F: Div is not part
 * of THREE namespace!` (hotfix `b8f7689`).
 *
 * The new architecture keeps the THREE meshes inside `<Canvas>` (where
 * they belong) and renders the DOM as plain JSX outside `<Canvas>`
 * (where R3F never sees it). Camera projection is bridged by
 * `cameraSnapshot.ts` — the world-space half writes the snapshot in
 * `useFrame`, the DOM half reads it for cursor-following layouts.
 *
 * # State surface
 *
 * Both halves accept a slice of the same `OverlayState` so the host
 * (`Scene3DStage`) can build it once and pass it through. Every field
 * is optional; absent fields = the corresponding overlay is hidden.
 */

import * as React from "react";
import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

import {
  getCameraSnapshot,
  setCameraSnapshot,
  subscribeCameraSnapshot,
  getCameraVersion,
} from "../cameraSnapshot";
import {
  createGridMesh,
  updateGridUniforms,
  type GridUniforms,
} from "./gridShader";
import {
  createSnapMarker,
  type SnapMarkerHandle,
  type SnapMarkerMode,
} from "./snapMarker";
import {
  createPolarGuide,
  type PolarGuideHandle,
} from "./polarGuide";
import {
  createOtrackGuide,
  type OtrackGuideHandle,
} from "./otrackGuide";
import {
  createOtrackMarker,
  type OtrackMarkerHandle,
} from "./otrackMarker";
import {
  createBeditFrame,
  type BeditFrameBounds,
  type BeditFrameHandle,
} from "./beditFrame";
import { SnapApertureBox } from "./snapApertureBox";
import { SelectionWindowRect } from "./selectionWindow";
import { ZoomRubberRect } from "./zoomRubber";
import { PrintPreviewBounds } from "./printPreviewBounds";
import { PickboxCursor } from "./PickboxCursor";
import { useSpaceContextStore } from "../../state/spaceContextStore";

// Selection-window, zoom-rubber and print-preview state interfaces live
// in `./overlayStateTypes.ts` so leaf overlay components can import them
// without re-importing the orchestrator (was a depcruise cycle source).
export type {
  SelectionWindowState,
  ZoomRubberRectState,
  PrintPreviewBoundsState,
} from "./overlayStateTypes";

import type {
  SelectionWindowState,
  ZoomRubberRectState,
  PrintPreviewBoundsState,
} from "./overlayStateTypes";

/**
 * State surface for the overlay layer. Every field is optional;
 * absent fields = the corresponding overlay is hidden.
 */
export interface OverlayState {
  /** Active OSNAP target (world-space). `null` hides the marker. */
  readonly snapTarget?: {
    mode: SnapMarkerMode;
    world: { x: number; y: number };
  } | null;
  /**
   * SnapTip label (e.g. "Endpoint", "Midpoint") rendered next to the
   * marker glyph when AUTOSNAP bit 2 is on. Caller-resolved string —
   * already i18n'd; we only paint it. `null` hides the tooltip.
   */
  readonly snapTip?: string | null;
  /** Cursor position in CSS pixels relative to the canvas parent. */
  readonly cursorClient?: { x: number; y: number } | null;
  /** Show the snap aperture box (OSNAPMODE > 0 + not dragging). */
  readonly showAperture?: boolean;
  /** PICKBOX sysvar value in CSS pixels. */
  readonly pickboxPx?: number;
  /** Polar tracking guide endpoints (offset-world coords). */
  readonly polarGuide?: {
    from: { x: number; y: number };
    to: { x: number; y: number };
  } | null;
  /**
   * Polar tracking "distance < angle°" tooltip (AutoCAD polar readout). Painted
   * near the cursor while a polar angle is locked; `null` hides it. Numeric /
   * locale-neutral — the caller pre-formats it.
   */
  readonly polarTip?: string | null;
  /**
   * OTRACK-SUBSYSTEM — engaged alignment-path guide lines (offset-world
   * coords), one entry per engaged ray (1 = single acquired-point path, 2 =
   * combined tracking). An ARRAY, not a single from/to pair — AutoCAD
   * genuinely renders two simultaneous dashed lines during combined
   * tracking (CLAUDE.md ПРИОРИТЕТ #1).
   */
  readonly otrackGuides?: ReadonlyArray<{
    from: { x: number; y: number };
    to: { x: number; y: number };
  }> | null;
  /**
   * OTRACK-SUBSYSTEM — acquired object-snap tracking points (offset-world
   * coords). Rendered as small "+" markers, PERSISTENT for as long as each
   * point stays acquired (independent of whether a path is currently
   * engaged near the cursor).
   */
  readonly otrackMarkers?: ReadonlyArray<{ x: number; y: number }> | null;
  /**
   * OTRACK tracking tooltip ("distance < angle°" per engaged ray, joined).
   * Painted near the cursor while OTRACK is engaged; `null` hides it.
   */
  readonly otrackTip?: string | null;
  /** BEDIT frame bounds (offset-world coords). */
  readonly beditBounds?: BeditFrameBounds | null;
  /** Show the BEDIT frame. */
  readonly beditActive?: boolean;
  /** Drag-rectangle selection window (window vs crossing). */
  readonly selectionWindow?: SelectionWindowState | null;
  /** ZOOM Window rubber-band rectangle. */
  readonly zoomRubber?: ZoomRubberRectState | null;
  /** Paper-space viewport print-preview bounds (model-space view). */
  readonly printPreviewBounds?: PrintPreviewBoundsState | null;
  /** Show the HTML crosshair. */
  readonly crosshairVisible?: boolean;
  /**
   * AutoCAD CURSORSIZE sysvar — crosshair length as a percentage of the
   * smaller viewport dimension (1..100). When omitted, defaults to 5
   * (the AutoCAD default → ±2.5 % from cursor centre). When 100 the
   * crosshair fills the viewport (alignment mode).
   */
  readonly cursorSizePct?: number;
  /**
   * Crosshair colour resolved by the host (`Scene3DStage`) from CURSORCOLOR
   * sysvar (ACI index → CSS colour). Defaults to AutoCAD parity contrast
   * (≈ rgba(170,170,170,.45)) when omitted so existing callers stay
   * pixel-identical.
   */
  readonly cursorColorCss?: string;
  /** AutoCAD PICKBOX selection-target box half-size in CSS px. Used by
   *  PickboxCursor sprite. When undefined, fallback to DEFAULT_PICKBOX_PX. */
  readonly selectionPickboxHalfPx?: number;
  /** True when no draft command is active (idle pick mode). Drives
   *  PickboxCursor visibility — AutoCAD shows the pickbox only in idle. */
  readonly idleSelectionMode?: boolean;
}

// Pure CURSORSIZE / CURSORCOLOR helpers live in `crosshair.ts` so the
// React-refresh `only-export-components` lint rule stays satisfied —
// this file exports React components, the helpers file exports pure
// utilities that share no state with the component tree. Re-exports
// are surfaced via `overlays/index.ts` directly from `./crosshair`.
import {
  crosshairHalfLengthPx,
  cursorColorCssFromAci,
} from "./crosshair";

/**
 * Default-empty `OverlayState` factory. Initialises all the new
 * AABB-style overlays to `null` so consumers that only care about a
 * subset can spread this and override.
 */
export function createDefaultOverlayState(): OverlayState {
  return {
    snapTarget: null,
    snapTip: null,
    cursorClient: null,
    showAperture: false,
    polarGuide: null,
    polarTip: null,
    otrackGuides: null,
    otrackMarkers: null,
    otrackTip: null,
    beditBounds: null,
    beditActive: false,
    selectionWindow: null,
    zoomRubber: null,
    printPreviewBounds: null,
    crosshairVisible: false,
    cursorSizePct: 5,
  };
}

export interface Scene3DOverlayLayerProps {
  readonly state: OverlayState;
}

/**
 * R3F-side overlay component. Mounts inside `<Canvas>`. Owns every
 * overlay that lives in the THREE scene tree (grid mesh, snap marker
 * group, polar guide line, BEDIT frame mesh) and keeps them in sync
 * with the camera + state on every frame.
 *
 * Returns `null` — meshes are added directly to the scene via
 * `useEffect`. R3F is happy with `null` children inside `<Canvas>`.
 */
export function Scene3DWorldOverlays(
  props: Scene3DOverlayLayerProps,
): null {
  const { scene, camera, invalidate, size } = useThree((state) => ({
    scene: state.scene,
    camera: state.camera,
    invalidate: state.invalidate,
    size: state.size,
  }));

  // AutoCAD parity: the model drawing-grid belongs to model space only.
  // PSPACE / MSPACE_VIA_VP are paper-space layout modes — the grid must
  // not render over the white paper sheet in either of those modes.
  const spaceMode = useSpaceContextStore((s) => s.mode);

  // ── World-space overlay handles (built once, retained across frames) ─
  const gridHandleRef = useRef<{
    mesh: THREE.Mesh;
    uniforms: GridUniforms;
  } | null>(null);
  const snapMarkerRef = useRef<SnapMarkerHandle | null>(null);
  const polarGuideRef = useRef<PolarGuideHandle | null>(null);
  const otrackGuideRef = useRef<OtrackGuideHandle | null>(null);
  const otrackMarkerRef = useRef<OtrackMarkerHandle | null>(null);
  const beditFrameRef = useRef<BeditFrameHandle | null>(null);

  useEffect(() => {
    const grid = createGridMesh();
    scene.add(grid.mesh);
    gridHandleRef.current = grid;

    const snap = createSnapMarker();
    scene.add(snap.group);
    snapMarkerRef.current = snap;

    const polar = createPolarGuide();
    scene.add(polar.line);
    polarGuideRef.current = polar;

    const otrackGuide = createOtrackGuide();
    scene.add(otrackGuide.group);
    otrackGuideRef.current = otrackGuide;

    const otrackMarker = createOtrackMarker();
    scene.add(otrackMarker.group);
    otrackMarkerRef.current = otrackMarker;

    const bedit = createBeditFrame();
    scene.add(bedit.mesh);
    beditFrameRef.current = bedit;

    invalidate();

    return () => {
      if (gridHandleRef.current) {
        scene.remove(gridHandleRef.current.mesh);
        gridHandleRef.current.mesh.geometry.dispose();
        (gridHandleRef.current.mesh.material as THREE.Material).dispose();
        gridHandleRef.current = null;
      }
      if (snapMarkerRef.current) {
        scene.remove(snapMarkerRef.current.group);
        snapMarkerRef.current.dispose();
        snapMarkerRef.current = null;
      }
      if (polarGuideRef.current) {
        scene.remove(polarGuideRef.current.line);
        polarGuideRef.current.dispose();
        polarGuideRef.current = null;
      }
      if (otrackGuideRef.current) {
        scene.remove(otrackGuideRef.current.group);
        otrackGuideRef.current.dispose();
        otrackGuideRef.current = null;
      }
      if (otrackMarkerRef.current) {
        scene.remove(otrackMarkerRef.current.group);
        otrackMarkerRef.current.dispose();
        otrackMarkerRef.current = null;
      }
      if (beditFrameRef.current) {
        scene.remove(beditFrameRef.current.mesh);
        beditFrameRef.current.dispose();
        beditFrameRef.current = null;
      }
    };
  }, [scene, invalidate]);

  // ── Per-frame: keep grid + camera-driven overlays in sync ────────────
  useFrame(() => {
    // Push camera snapshot for sibling DOM overlays (grips, crosshair,
    // aperture). Cheap — pointer-equal ref + version bump only.
    setCameraSnapshot(camera, size);

    const grid = gridHandleRef.current;
    if (grid) {
      // Hide the grid in paper-space layout modes so it does not dim the
      // white paper sheet. AutoCAD never shows the model grid on layouts.
      grid.mesh.visible = spaceMode === "MSPACE";
    }
    if (grid && (camera as THREE.OrthographicCamera).isOrthographicCamera) {
      updateGridUniforms(grid.uniforms, camera as THREE.OrthographicCamera);
    }
    // Snap marker scale follows camera zoom — update each frame so
    // panning while snap target is active keeps the glyph at constant
    // CSS-pixel size. Cheap (group.scale uniform write).
    const snap = snapMarkerRef.current;
    const target = props.state.snapTarget;
    if (snap && target) {
      const zoom = (camera as THREE.OrthographicCamera).zoom ?? 1;
      snap.setActive(target.mode, target.world, zoom);
    } else if (snap) {
      snap.setHidden();
    }
    // Polar guide endpoints (already in world coords; update freely).
    const polar = polarGuideRef.current;
    if (polar && props.state.polarGuide) {
      polar.setEndpoints(
        props.state.polarGuide.from,
        props.state.polarGuide.to,
      );
    } else if (polar) {
      polar.setHidden();
    }
    // OTRACK alignment-path guide lines (already in world coords).
    const otrackGuide = otrackGuideRef.current;
    if (otrackGuide && props.state.otrackGuides && props.state.otrackGuides.length > 0) {
      otrackGuide.setSegments(props.state.otrackGuides);
    } else if (otrackGuide) {
      otrackGuide.setHidden();
    }
    // OTRACK acquired-point "+" markers (persistent while acquired).
    const otrackMarker = otrackMarkerRef.current;
    if (otrackMarker && props.state.otrackMarkers && props.state.otrackMarkers.length > 0) {
      const zoom = (camera as THREE.OrthographicCamera).zoom ?? 1;
      otrackMarker.setPoints(props.state.otrackMarkers, zoom);
    } else if (otrackMarker) {
      otrackMarker.setHidden();
    }
    // BEDIT frame.
    const bedit = beditFrameRef.current;
    const beditOn = props.state.beditActive === true && props.state.beditBounds;
    if (bedit && beditOn && props.state.beditBounds) {
      const zoom = (camera as THREE.OrthographicCamera).zoom ?? 1;
      const safeZoom = Math.max(zoom, 1e-6);
      bedit.setBounds(props.state.beditBounds, 8 / safeZoom, zoom);
    } else if (bedit) {
      bedit.setHidden();
    }
  });

  return null;
}

/**
 * DOM-side overlay component. Mounted as a sibling of `<Canvas>`,
 * NOT inside it — R3F's reconciler must never see these `<div>` nodes
 * (PR-K.1 root cause).
 *
 * Pure JSX render. State drives visibility for each overlay; cursor
 * coords are pre-computed by the host (`Scene3DStage`) from
 * `pointermove` listeners on the stage shell, so this component does
 * not need direct camera access.
 *
 * Sits above the canvas via absolute positioning + z-index. Pointer
 * events disabled at the wrapper level so clicks pass through to the
 * canvas; individual overlays opt in only where they need pointer
 * capture (none currently).
 */
export function Scene3DDomOverlays(
  props: Scene3DOverlayLayerProps,
): React.ReactElement {
  // Subscribe to camera snapshot version so the crosshair reflows on
  // canvas resize. The snapshot's `size` field carries the live
  // viewport dimensions in CSS pixels, kept fresh every frame by
  // `Scene3DWorldOverlays` (which mirrors size into the snapshot from
  // R3F's `useThree(state.size)`).
  React.useSyncExternalStore(
    subscribeCameraSnapshot,
    getCameraVersion,
    () => 0,
  );
  const snap = getCameraSnapshot();
  const canvasWidth = snap?.size.width ?? 0;
  const canvasHeight = snap?.size.height ?? 0;

  // HTML crosshair: AutoCAD CURSORSIZE parity. Two short lines centred
  // on the cursor whose half-length is `pct/100 × min(w,h) / 2`. At
  // CURSORSIZE=100 the half-length saturates to half the smaller
  // viewport dim → both lines extend across the whole canvas (alignment
  // mode). Pure CSS, no GL.
  const cursorSizePct = props.state.cursorSizePct ?? 5;
  const cursorColor =
    props.state.cursorColorCss ?? cursorColorCssFromAci(7);
  const halfLen = crosshairHalfLengthPx(
    cursorSizePct,
    canvasWidth,
    canvasHeight,
  );
  const isFullScreen = cursorSizePct >= 100;
  const crosshair =
    props.state.crosshairVisible && props.state.cursorClient ? (
      <>
        <div
          data-testid="scene3d-crosshair-h"
          data-crosshair-pct={String(cursorSizePct)}
          style={{
            position: "absolute",
            // Full-screen alignment mode: extend lines edge-to-edge.
            // Otherwise: short segment centred on the cursor.
            left: isFullScreen
              ? 0
              : Math.max(0, props.state.cursorClient.x - halfLen),
            top: props.state.cursorClient.y,
            width: isFullScreen ? canvasWidth : halfLen * 2,
            height: 1,
            background: cursorColor,
            pointerEvents: "none",
            zIndex: 3,
          }}
        />
        <div
          data-testid="scene3d-crosshair-v"
          data-crosshair-pct={String(cursorSizePct)}
          style={{
            position: "absolute",
            left: props.state.cursorClient.x,
            top: isFullScreen
              ? 0
              : Math.max(0, props.state.cursorClient.y - halfLen),
            width: 1,
            height: isFullScreen ? canvasHeight : halfLen * 2,
            background: cursorColor,
            pointerEvents: "none",
            zIndex: 3,
          }}
        />
      </>
    ) : null;

  // exactOptionalPropertyTypes: pass optional fields only when defined.
  const apertureProps =
    props.state.pickboxPx !== undefined
      ? {
          cursorClient: props.state.cursorClient ?? null,
          pickboxPx: props.state.pickboxPx,
          hidden: !(props.state.showAperture ?? false),
        }
      : {
          cursorClient: props.state.cursorClient ?? null,
          hidden: !(props.state.showAperture ?? false),
        };
  // Print-preview bounds: the new `printPreviewBounds` field carries
  // a projected AABB in {x, y, width, height} form; PrintPreviewBounds
  // expects {left, top, right, bottom}. Convert here so the projection
  // shape stays caller-friendly.
  const ppb = props.state.printPreviewBounds;
  const ppbBounds =
    ppb && ppb.visible
      ? {
          left: ppb.rectScreen.x,
          top: ppb.rectScreen.y,
          right: ppb.rectScreen.x + ppb.rectScreen.width,
          bottom: ppb.rectScreen.y + ppb.rectScreen.height,
        }
      : null;
  const printPreviewProps =
    ppb?.paperSize !== undefined
      ? { bounds: ppbBounds, label: ppb.paperSize }
      : { bounds: ppbBounds };
  return (
    <div
      data-testid="scene3d-overlay-portal"
      className="scene3d-overlay-portal"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {crosshair}
      <PickboxCursor
        cursorClient={props.state.cursorClient ?? null}
        crosshairVisible={props.state.crosshairVisible ?? false}
        idleSelectionMode={props.state.idleSelectionMode ?? false}
        halfPx={props.state.selectionPickboxHalfPx ?? 3}
        cursorColorCss={props.state.cursorColorCss}
      />
      <SnapApertureBox {...apertureProps} />
      {props.state.snapTip && props.state.cursorClient ? (
        <div
          data-testid="scene3d-snap-tip"
          style={{
            position: "absolute",
            left: props.state.cursorClient.x + 16,
            top: props.state.cursorClient.y + 18,
            background: "rgba(20, 20, 20, 0.85)",
            color: "#f1c40f",
            padding: "2px 6px",
            borderRadius: 3,
            fontSize: 11,
            fontFamily: "system-ui, sans-serif",
            pointerEvents: "none",
            zIndex: 4,
            whiteSpace: "nowrap",
          }}
        >
          {props.state.snapTip}
        </div>
      ) : null}
      {props.state.polarTip && props.state.cursorClient ? (
        <div
          data-testid="scene3d-polar-tip"
          style={{
            position: "absolute",
            // Offset below-left of the cursor so it does not overlap the
            // OSNAP SnapTip (which sits below-right).
            left: props.state.cursorClient.x + 16,
            top: props.state.cursorClient.y + 34,
            background: "rgba(20, 20, 20, 0.85)",
            // Teal-green — matches the polar guide line colour.
            color: "#00e07a",
            padding: "2px 6px",
            borderRadius: 3,
            fontSize: 11,
            fontFamily: "system-ui, sans-serif",
            pointerEvents: "none",
            zIndex: 4,
            whiteSpace: "nowrap",
          }}
        >
          {props.state.polarTip}
        </div>
      ) : null}
      {props.state.otrackTip && props.state.cursorClient ? (
        <div
          data-testid="scene3d-otrack-tip"
          style={{
            position: "absolute",
            // Offset further below than the OSNAP/POLAR tooltips so all
            // three can be visible simultaneously without overlapping.
            left: props.state.cursorClient.x + 16,
            top: props.state.cursorClient.y + 50,
            background: "rgba(20, 20, 20, 0.85)",
            // Sky-blue — matches the OTRACK guide line colour.
            color: "#3fa9ff",
            padding: "2px 6px",
            borderRadius: 3,
            fontSize: 11,
            fontFamily: "system-ui, sans-serif",
            pointerEvents: "none",
            zIndex: 4,
            whiteSpace: "nowrap",
          }}
        >
          {props.state.otrackTip}
        </div>
      ) : null}
      <SelectionWindowRect state={props.state.selectionWindow ?? null} />
      <ZoomRubberRect state={props.state.zoomRubber ?? null} />
      <PrintPreviewBounds {...printPreviewProps} />
    </div>
  );
}
